import React, { useState } from "react"

import {
	Button,
	CircularProgress,
	Grid,
	Typography
} from "@material-ui/core"

import { CustomTextField, Notification } from "@/components"
import { AccountAdminApi } from "@/services/Api"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import { formatEmail } from "@/utils/mask"

import useStyles from "@/pages/Tools/ToolsPages/CreateAdminAccountToolPage/styles"

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const CreateAdminAccountToolPage = () => {
	const classes = useStyles()

	const [name, setName] = useState("")
	const [email, setEmail] = useState("")
	const [isCreatingAccount, setIsCreatingAccount] = useState(false)

	const { validation, triggerValidation, clearValidation } = useValidation()

	const isEmailFilled = email !== ""
	const isValidEmailFormat = EMAIL_FORMAT_REGEX.test(email)
	const isValidName = name.trim().length > 1

	const emailFormatErrorMessage = isEmailFilled && !isValidEmailFormat ? "Informe um e-mail válido." : ""
	const emailErrorMessage = validation.email ?? emailFormatErrorMessage
	const isCreateAccountButtonDisabled = !isValidName || !isValidEmailFormat || isCreatingAccount

	const handleNameChange = (value: string) => {
		setName(value)
		clearValidation("name")
	}

	const handleEmailChange = (value: string) => {
		setEmail(formatEmail(value))
		clearValidation("email")
	}

	const handleCreateAccount = async () => {
		setIsCreatingAccount(true)

		try {
			await AccountAdminApi.post(
				"/auth/internal/create-account",
				{ name, email }
			)

			Notification.success({ message: `Conta criada e e-mail de acesso enviado para ${email}` })
			setName("")
			setEmail("")
		} catch (error) {
			triggerValidation(error as ErrorType)
		} finally {
			setIsCreatingAccount(false)
		}
	}

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Typography color="textPrimary" variant="h4">
					Criar conta de administrador
				</Typography>
			</Grid>

			<Grid item xs={12}>
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Typography color="textSecondary" variant="subtitle1">
							Informe o nome e o e-mail do novo administrador. <strong>A senha é gerada automaticamente</strong> e o acesso é definido pelo próprio administrador via e-mail.
						</Typography>
					</Grid>

					<Grid item>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<CustomTextField
									label="Nome completo"
									placeholder="Ex.: Maria Souza"
									variant="outlined"
									fullWidth
									value={name}
									onChange={({ target }) => handleNameChange(target.value)}
									error={Boolean(validation.name)}
									helperText={validation.name}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<CustomTextField
									label="E-mail"
									placeholder="nome@letalk.com.br"
									variant="outlined"
									fullWidth
									value={email}
									onChange={({ target }) => handleEmailChange(target.value)}
									error={Boolean(emailErrorMessage)}
									helperText={emailErrorMessage}
								/>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						<Typography variant="h5">
							*A conta é criada com permissão de administrador do painel interno.
						</Typography>
					</Grid>

					<Grid item>
						<Button
							color="primary"
							variant="contained"
							className={classes.createAccountButton}
							disabled={isCreateAccountButtonDisabled}
							onClick={handleCreateAccount}
							endIcon={isCreatingAccount && <CircularProgress size={20} color="inherit" />}
						>
							{isCreatingAccount ? "CRIANDO CONTA…" : "CRIAR CONTA"}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default CreateAdminAccountToolPage
